import { useEffect, useState, useMemo } from "react";
import { Loader2, Plus, Soup } from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Checkbox } from "@project/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { MobileNumberField } from "@project/components/mobile-number-field";
import { getApiErrorMessage } from "@project/lib/api-error";
import { projectToast } from "@project/lib/toast";
import { tiffinSevaSchemeService } from "@project/services/admin/tiffin-seva-scheme.service";
import { tiffinSevaDonationService, type TiffinSevaAddDonationPayload } from "@project/services/admin/tiffin-seva-donation.service";

type FormState = {
  scheme: string;
  times: string;
  donor_name: string;
  mobile_number: string;
  email: string;
  city: string;
  full_address: string;
  state: string;
  pincode: string;
  country: string;
  occasion: string;
  display_name: string;
  special_instructions: string;
  is_anonymous: boolean;
  info_confirmed: boolean;
  contact_consent: boolean;
  payment_mode: string;
  payment_reference: string;
};

const initialForm: FormState = {
  scheme: "",
  times: "1",
  donor_name: "",
  mobile_number: "",
  email: "",
  city: "Ahmedabad",
  full_address: "",
  state: "Gujarat",
  pincode: "",
  country: "India",
  occasion: "",
  display_name: "",
  special_instructions: "",
  is_anonymous: false,
  info_confirmed: false,
  contact_consent: false,
  payment_mode: "cash",
  payment_reference: "",
};

type TiffinSevaAddDonationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export function TiffinSevaAddDonationDialog({
  open,
  onOpenChange,
  onSuccess,
}: TiffinSevaAddDonationDialogProps) {
  const [form, setForm] = useState<FormState>(initialForm);
  const [schemes, setSchemes] = useState<any[]>([]);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setSubmitting(false);
      setLoadingSchemes(true);
      
      tiffinSevaSchemeService.listPage({ page_size: 100 })
        .then(res => {
          const active = res.data.filter((s: any) => s.is_active);
          setSchemes(active);
          if (active.length > 0) {
            setForm(prev => ({ ...prev, scheme: String(active[0].id) }));
          }
        })
        .catch(err => {
          console.error("Failed to load tiffin schemes:", err);
          projectToast.error("Could not load donation schemes");
        })
        .finally(() => setLoadingSchemes(false));
    }
  }, [open]);

  const patch = (updates: Partial<FormState>) =>
    setForm(prev => ({ ...prev, ...updates }));

  const selectedSchemeObj = useMemo(() => {
    return schemes.find(s => String(s.id) === form.scheme);
  }, [schemes, form.scheme]);

  const estimatedTotal = useMemo(() => {
    if (!selectedSchemeObj) return 0;
    const priceNum = parseFloat(selectedSchemeObj.price);
    const timesNum = parseInt(form.times, 10) || 0;
    return priceNum * timesNum;
  }, [selectedSchemeObj, form.times]);

  const buildPayload = (): TiffinSevaAddDonationPayload => {
    if (!form.scheme) {
      throw new Error("Please select a donation scheme.");
    }
    const timesParsed = parseInt(form.times, 10);
    if (Number.isNaN(timesParsed) || timesParsed <= 0) {
      throw new Error("Please enter a valid number of times.");
    }
    if (!form.donor_name.trim()) {
      throw new Error("Donor Name is required.");
    }
    if (!/^\d{10}$/.test(form.mobile_number.trim())) {
      throw new Error("Enter a valid 10-digit mobile number.");
    }

    return {
      scheme: Number(form.scheme),
      times: timesParsed,
      donor_name: form.donor_name.trim(),
      mobile_number: form.mobile_number.trim(),
      email: form.email.trim(),
      city: form.city.trim(),
      full_address: form.full_address.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
      country: form.country.trim() || "India",
      occasion: form.occasion.trim() || undefined,
      display_name: form.display_name.trim() || undefined,
      special_instructions: form.special_instructions.trim() || undefined,
      is_anonymous: form.is_anonymous,
      info_confirmed: form.info_confirmed,
      contact_consent: form.contact_consent,
      payment_mode: form.payment_mode,
      payment_reference: form.payment_reference.trim() || "CASH",
      mode: "offline",
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = buildPayload();
      setSubmitting(true);
      await tiffinSevaDonationService.add(payload);
      projectToast.success("Offline donation recorded successfully.");
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      projectToast.error(getApiErrorMessage(err, "Failed to add offline donation"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
        <DialogHeader className="shrink-0 border-b border-border px-5 py-4 pr-12 text-left sm:px-6">
          <div className="flex items-start gap-3">
            <div className="bg-primary/10 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-primary">
              <Soup className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-foreground sm:text-xl">
                Add Offline Tiffin Donation
              </DialogTitle>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Record an offline cash/cheque tiffin seva donation in the system
              </p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="min-h-0 flex-1 flex flex-col overflow-hidden">
          <div className="min-h-0 flex-1 space-y-7 overflow-y-auto px-5 py-5 sm:px-6">
            
            {/* Sponsorship scheme details */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                1. Sponsorship Details
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label data-required="true">Sponsorship Scheme</Label>
                  <Select
                    value={form.scheme}
                    onValueChange={scheme => patch({ scheme })}
                    disabled={loadingSchemes || schemes.length === 0}
                  >
                    <SelectTrigger className="bg-muted/30 rounded-xl">
                      <SelectValue placeholder={loadingSchemes ? "Loading..." : "Select Scheme"} />
                    </SelectTrigger>
                    <SelectContent>
                      {schemes.map(s => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.name} (₹{parseFloat(s.price).toLocaleString("en-IN")})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label data-required="true">Times (Multiplier)</Label>
                  <Input
                    required
                    type="number"
                    min="1"
                    value={form.times}
                    onChange={e => patch({ times: e.target.value })}
                    className="bg-muted/30 rounded-xl"
                  />
                </div>

                {selectedSchemeObj && (
                  <div className="sm:col-span-2 bg-muted/40 border border-border rounded-xl p-3.5 flex items-center justify-between">
                    <span className="text-xs font-semibold text-foreground">
                      Calculated Donation Amount
                    </span>
                    <span className="text-sm font-bold text-primary">
                      ₹{parseFloat(selectedSchemeObj.price).toLocaleString("en-IN")} × {form.times || 1} = ₹{estimatedTotal.toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
              </div>
            </section>

            {/* Donor info */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                2. Donor Details
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label data-required="true">Donor Name</Label>
                  <Input
                    required
                    value={form.donor_name}
                    onChange={e => patch({ donor_name: e.target.value })}
                    placeholder="John Doe"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">Mobile Number</Label>
                  <MobileNumberField
                    required
                    value={form.mobile_number}
                    onChange={mobile_number => patch({ mobile_number })}
                    placeholder="10-digit phone number"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={e => patch({ email: e.target.value })}
                    placeholder="name@example.com"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={form.city}
                    onChange={e => patch({ city: e.target.value })}
                    placeholder="Ahmedabad"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Full Address</Label>
                  <Textarea
                    rows={2}
                    value={form.full_address}
                    onChange={e => patch({ full_address: e.target.value })}
                    placeholder="Street name, society, flat number"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    value={form.state}
                    onChange={e => patch({ state: e.target.value })}
                    placeholder="Gujarat"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Pincode</Label>
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    value={form.pincode}
                    onChange={e =>
                      patch({
                        pincode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      })
                    }
                    placeholder="380001"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
              </div>
            </section>

            {/* Payment Details */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                3. Offline Payment Information
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label data-required="true">Payment Mode</Label>
                  <Select
                    value={form.payment_mode}
                    onValueChange={payment_mode => patch({ payment_mode })}
                  >
                    <SelectTrigger className="bg-muted/30 rounded-xl">
                      <SelectValue placeholder="Select Mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="online">Online Adjustment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Transaction Reference / Cheque No.</Label>
                  <Input
                    value={form.payment_reference}
                    onChange={e => patch({ payment_reference: e.target.value })}
                    placeholder="e.g. CHQ-99238 or Bank TXN ref"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
              </div>
            </section>

            {/* Commemoration details */}
            <section className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                4. Additional Commemoration
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Occasion</Label>
                  <Input
                    value={form.occasion}
                    onChange={e => patch({ occasion: e.target.value })}
                    placeholder="e.g. Birthday, Anniversary"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Name to Display on Board</Label>
                  <Input
                    value={form.display_name}
                    onChange={e => patch({ display_name: e.target.value })}
                    placeholder="In memory of... / Family name"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Special Instructions</Label>
                  <Textarea
                    rows={2}
                    value={form.special_instructions}
                    onChange={e => patch({ special_instructions: e.target.value })}
                    placeholder="Special request notes..."
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2 space-y-3">
                  <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-muted/10 px-4 py-3">
                    <Checkbox
                      checked={form.is_anonymous}
                      onCheckedChange={checked =>
                        patch({ is_anonymous: checked === true })
                      }
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-sm font-semibold">
                        Keep this donation anonymous
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        Hides the donor name from public display boards
                      </span>
                    </span>
                  </label>

                  <div className="rounded-xl border border-border bg-muted/10 p-4 space-y-4">
                    <label className="flex cursor-pointer items-start gap-3">
                      <Checkbox
                        checked={form.info_confirmed}
                        onCheckedChange={checked =>
                          patch({ info_confirmed: checked === true })
                        }
                        className="mt-0.5"
                      />
                      <span>
                        <span className="block text-sm font-semibold">
                          Information confirmed <span className="text-destructive">*</span>
                        </span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          Confirm that all entered information is accurate
                        </span>
                      </span>
                    </label>

                    <label className="flex cursor-pointer items-start gap-3">
                      <Checkbox
                        checked={form.contact_consent}
                        onCheckedChange={checked =>
                          patch({ contact_consent: checked === true })
                        }
                        className="mt-0.5"
                      />
                      <span>
                        <span className="block text-sm font-semibold">
                          Contact consent for this sponsorship
                        </span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          Consent to be contacted regarding this sponsorship
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </section>

          </div>

          <DialogFooter className="shrink-0 gap-2 border-t border-border px-5 py-4 sm:justify-end sm:px-6">
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={() => onOpenChange(false)}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || schemes.length === 0}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl"
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Donation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
